import type { PointerEvent as ReactPointerEvent } from 'react';
import type { Draft } from '../shared/types';
import type { PlacementCorner } from './placement';

export interface PreparedSourceHandlesProps {
  draft: Draft;
  /** Surface origin in the surrounding SVG coordinate space. */
  origin: { x: number; y: number };
  /** 22 CSS pixels expressed in that same coordinate space. */
  handleRadius: number;
  panning?: boolean;
  onBodyPointerDown?: (event: ReactPointerEvent<SVGRectElement>) => void;
  onCornerPointerDown?: (event: ReactPointerEvent<SVGCircleElement>, corner: PlacementCorner) => void;
  onRotationPointerDown?: (event: ReactPointerEvent<SVGCircleElement>) => void;
}

/** Shared selection UI only. The caller retains pointer ownership and uses the
 * common placement helpers for movement, aspect-preserving resize and rotation. */
export default function PreparedSourceHandles({ draft, origin, handleRadius, panning = false, onBodyPointerDown, onCornerPointerDown, onRotationPointerDown }: PreparedSourceHandlesProps) {
  const left = origin.x + draft.x, top = origin.y + draft.y;
  const centerX = left + draft.width / 2, centerY = top + draft.height / 2;
  const rotationY = top - handleRadius * 2;
  return <g data-editor-overlay="placement-handles" data-placement-gesture="true" pointerEvents="none" transform={`rotate(${draft.rotation ?? 0} ${centerX} ${centerY})`}>
    <rect data-placement-body="true" x={left} y={top} width={draft.width} height={draft.height} fill="transparent" pointerEvents={panning ? 'none' : 'all'} style={{ cursor: 'move' }} stroke="#fff3d6" strokeWidth="2" strokeDasharray="5 3" vectorEffect="non-scaling-stroke" onPointerDown={onBodyPointerDown} />
    <line x1={centerX} y1={top} x2={centerX} y2={rotationY} stroke="#fff3d6" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    <circle data-placement-rotation="true" cx={centerX} cy={rotationY} r={handleRadius} fill="transparent" pointerEvents="all" style={{ cursor: 'grab' }} onPointerDown={onRotationPointerDown}><title>Rotate artwork</title></circle>
    <circle cx={centerX} cy={rotationY} r={handleRadius * .32} fill="#fff3d6" stroke="#315b52" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    {([[0, 0], [1, 0], [1, 1], [0, 1]] as const).map(([x, y], corner) => {
      const sourceX = left + x * draft.width, sourceY = top + y * draft.height;
      // Full finger-sized targets stay outside even a tiny prepared source.
      const handleX = sourceX + (x ? 1 : -1) * handleRadius / Math.SQRT2;
      const handleY = sourceY + (y ? 1 : -1) * handleRadius / Math.SQRT2;
      return <g key={corner}>
        <line x1={sourceX} y1={sourceY} x2={handleX} y2={handleY} stroke="#fff3d6" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
        <circle data-placement-corner={corner} cx={handleX} cy={handleY} r={handleRadius} fill="transparent" pointerEvents="all" style={{ cursor: corner % 2 ? 'nesw-resize' : 'nwse-resize' }} onPointerDown={event => onCornerPointerDown?.(event, corner as PlacementCorner)}><title>Resize artwork</title></circle>
        <circle cx={handleX} cy={handleY} r={handleRadius * 4 / 22} fill="#fff3d6" stroke="#315b52" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      </g>;
    })}
  </g>;
}
